import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Tooltip,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import { useOrderSession, sessionItemToCartItem } from "../Context/OrderSessionContext";
import { useCart } from "../Context/CartContext";
import { useDiscount } from "../Context/DiscountContext";
import { useCurrency } from "../Context/CurrencyContext";
import { useCompany } from "../Context/CompanyContext";
import * as api from "../api/index";
import KOTDialog from "./KOTDialog";

export default function OpenHoldsPanel({ open, onClose, onLoadSuccess, onLoadError }) {
  const {
    openSessions,
    fetchOpenSessions,
    loadSession,
    loading,
    refreshActiveSession,
    activeSessionId,
  } = useOrderSession();
  const { setCartItems } = useCart();
  const { clearDiscount } = useDiscount();
  const { currency } = useCurrency();
  const [selecting, setSelecting] = useState(null);
  const [kotDialogOpen, setKotDialogOpen] = useState(false);
  const [selectedSessionForKot, setSelectedSessionForKot] = useState(null);
  const [loadingKOTSession, setLoadingKOTSession] = useState(false);
  const { company } = useCompany();
  const holdFeatureEnabled = !!company?.enableHoldKot;
  

  useEffect(() => {
    if (open && holdFeatureEnabled) fetchOpenSessions();
  }, [open, fetchOpenSessions, holdFeatureEnabled]);

  const handleSelectSession = async (session) => {
    setSelecting(session._id);
    try {
      const data = await loadSession(session._id);
      const cartItems = (data.items || [])
        .map((it) => sessionItemToCartItem(it))
        .filter(Boolean);
      setCartItems(cartItems);
      clearDiscount?.();
      onClose();
      onLoadSuccess?.(session.label);
    } catch (err) {
      console.error(err);
      onLoadError?.(err?.response?.data?.message || "Failed to load hold");
    } finally {
      setSelecting(null);
    }
  };

  const handleOpenKOT = async (e, session) => {
    if (!holdFeatureEnabled) return;
    e.stopPropagation();
    if (!session?._id) {
      onLoadError?.("Invalid session");
      return;
    }
    setLoadingKOTSession(true);
    try {
      const data = await loadSession(session._id);
      setSelectedSessionForKot(data);
      setKotDialogOpen(true);
    } catch (err) {
      console.error("[OpenHoldsPanel] Failed to load session for KOT:", err);
      onLoadError?.("Failed to load session for KOT");
    } finally {
      setLoadingKOTSession(false);
    }
  };

  const handleKOTSuccess = async (message) => {
    onLoadSuccess?.(message);
    await fetchOpenSessions();
    setKotDialogOpen(false);
    setSelectedSessionForKot(null);
  };

  const handleKOTError = (message) => {
    onLoadError?.(message);
  };

  const getUnprintedCount = (session) => {
    return (session.items || []).filter((item) => !item.kotPrinted).length;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        style={{ zIndex: 10000 }}
        PaperProps={{ style: { zIndex: 10000, position: "relative" } }}
      >
        <DialogTitle disableTypography style={{ padding: "16px 24px", position: "relative", zIndex: 1 }}>
          <Box display="flex" alignItems="center" width="100%" minWidth={0}>
            <Box flex={1} minWidth={0} style={{ textAlign: "left" }}>
              <Typography variant="h6" style={{ fontWeight: 700, fontSize: "1.25rem", color: "#333" }}>
                Open Holds
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} style={{ flexShrink: 0, marginLeft: 16 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 24, position: "relative", zIndex: 1 }} className="slim-scrollbar">
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : !openSessions.length ? (
            <Typography color="textSecondary" align="center" style={{ padding: 32 }}>
              No open holds yet.
            </Typography>
          ) : (
            <Grid container spacing={2} style={{ position: "relative", zIndex: 1 }}>
              {openSessions.map((s) => {
                const unprintedCount = getUnprintedCount(s);
                return (
                  <Grid item xs={6} sm={4} key={s._id} style={{ position: "relative", zIndex: 1 }}>
                    <Card
                      variant="outlined"
                      style={{
                        cursor: "pointer",
                        border: "2px solid #ef7f1a",
                        borderRadius: 12,
                        textAlign: "center",
                        position: "relative",
                        zIndex: 1,
                      }}
                      onClick={() => !selecting && handleSelectSession(s)}
                    >
                      {holdFeatureEnabled && unprintedCount > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 10,
                          }}
                        >
                          <Tooltip 
                            title={`${unprintedCount} new item${unprintedCount !== 1 ? "s" : ""} - Click to open KOT`}
                            arrow
                            PopperProps={{
                              style: { zIndex: 10002 },
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenKOT(e, s)}
                              disabled={loadingKOTSession}
                              style={{
                                backgroundColor: loadingKOTSession ? "#ffb74d" : "#ff9800",
                                color: "white",
                                padding: 6,
                                position: "relative",
                                zIndex: 10,
                                cursor: loadingKOTSession ? "wait" : "pointer",
                              }}
                            >
                              {loadingKOTSession ? (
                                <CircularProgress size={16} style={{ color: "white" }} />
                              ) : (
                                <>
                                  <RestaurantIcon fontSize="small" />
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: -4,
                                      right: -4,
                                      backgroundColor: "#f44336",
                                      color: "white",
                                      borderRadius: "50%",
                                      width: 18,
                                      height: 18,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 10,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {unprintedCount}
                                  </span>
                                </>
                              )}
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                      <CardContent style={{ paddingBottom: 8, position: "relative", zIndex: 1 }}>
                        <Typography variant="h6" style={{ fontWeight: 600, wordBreak: "break-word" }}>
                          {s.label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {s.items?.length ?? 0} item{(s.items?.length ?? 0) !== 1 ? "s" : ""}
                          {unprintedCount > 0 && (
                            <span style={{ color: "#ff9800", fontWeight: 500 }}>
                              {" "}({unprintedCount} new)
                            </span>
                          )}
                        </Typography>
                        <Typography variant="subtitle1" style={{ fontWeight: 500, marginTop: 4 }}>
                          {currency}{Number(s.grandTotal || 0).toFixed(2)}
                        </Typography>
                      </CardContent>
                      <CardActions style={{ justifyContent: "center", paddingTop: 0, position: "relative", zIndex: 1 }}>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          disabled={selecting === s._id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectSession(s);
                          }}
                        >
                          {selecting === s._id ? "Loading…" : "Open"}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      
      {holdFeatureEnabled && (
        <KOTDialog
          open={kotDialogOpen}
          onClose={() => {
            setKotDialogOpen(false);
            setSelectedSessionForKot(null);
          }}
          session={selectedSessionForKot}
          refreshSession={async () => {
            if (!selectedSessionForKot?._id) return;
            const { data } = await api.getOrderSession(selectedSessionForKot._id);
            setSelectedSessionForKot(data);
            if (activeSessionId === selectedSessionForKot._id) {
              await refreshActiveSession();
            }
          }}
          onSuccess={handleKOTSuccess}
          onError={handleKOTError}
        />
      )}
    </>
  );
}
